import type { BrowserBroadcast } from '@adapters/broadcast/browserBroadcast';
import { WebauthPopupPage } from '@adapters/navigation/page';
import { Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import type { Logger } from '@core/model/domain/logger';
import { SaveWebauthBankConnection } from '@core/usecase/saveWebauthBankConnection';
import { StoreService } from '@shared/services/store.service';
import { TrackerService } from '@shared/services/tracker.service';
import { firstValueFrom } from 'rxjs/internal/firstValueFrom';
import { browserBroadcastToken, loggerToken } from 'src/conf/di/angularProviderMapper';
import { Buffer } from 'buffer';
import { ConnectionToExternalBankKoComponent } from './pages/connection-to-external-bank-ko/connection-to-external-bank-ko.component';
import { ConnectionToExternalBankOkComponent } from './pages/connection-to-external-bank-ok/connection-to-external-bank-ok.component';
import { WebauthPopupVM } from './webauth-popup.viewmodel';

@Component({
  selector: 'app-webauth-popup',
  templateUrl: './webauth-popup.component.html',
  standalone: true,
  imports: [ConnectionToExternalBankOkComponent, ConnectionToExternalBankKoComponent],
})
export class WebauthPopupComponent implements OnInit {
  isServerOutcome = false;

  private webauthPopupVM: WebauthPopupVM;

  constructor(
    private readonly activatedRoute: ActivatedRoute,
    private readonly saveBankConnectionUsecase: SaveWebauthBankConnection,
    @Inject(browserBroadcastToken) private broadcastService: BrowserBroadcast,
    private tracker: TrackerService,
    @Inject(loggerToken) private loggerService: Logger,
    private storeService: StoreService
  ) {
    this.webauthPopupVM = new WebauthPopupVM(
      this.saveBankConnectionUsecase,
      this.broadcastService,
      this.loggerService
    );
  }

  async ngOnInit(): Promise<void> {
    const {
      id_connection: connectionId,
      state: userTokenAndTagging,
      error,
      error_description,
      outcome,
    } = (await firstValueFrom(this.activatedRoute.queryParams)) as Record<string, string>;

    if (outcome === 'success' || outcome === 'failure') {
      this.isServerOutcome = true;
      this.webauthPopupVM.handleServerOutcome(outcome);
      return;
    }

    if (!userTokenAndTagging) {
      this.webauthPopupVM.dispatchWebauthFail(new Error('WebauthPopupComponent: No state query param'));
      return;
    }

    const { token, tagging } = JSON.parse(Buffer.from(userTokenAndTagging, 'base64').toString('utf-8')) as {
      token: string | null;
      tagging: string | null;
    };

    await this.initializeTaggingTracker(tagging);

    if (error) {
      const selectedBankName = this.storeService.getVariable('SELECTED_BANK')?.name;
      this.webauthPopupVM.dispatchWebauthFail(
        new Error(`SelectedBank: ${selectedBankName} - Error:${error}: ${error_description}`)
      );
      return;
    }

    if (!connectionId) {
      this.webauthPopupVM.dispatchWebauthFail(
        new Error('WebauthPopupComponent: No connection id query param')
      );
      return;
    }

    if (token === null) {
      this.webauthPopupVM.dispatchWebauthFail(new Error('WebauthPopupComponent: token should be provided'));
      return;
    }

    await this.webauthPopupVM.onInit(connectionId, token);
  }

  private async initializeTaggingTracker(tagging: string | null) {
    if (tagging != null) {
      await this.tracker.initializeTaggingTracker(tagging);
    } else {
      this.loggerService.logTrace('WEBAUTH_POPUP: No tagging query param');
    }
  }

  get webauthPopupActualPage(): WebauthPopupPage | undefined {
    return this.webauthPopupVM.webauthPopupActualPage?.value;
  }
}
